// The copy-pastable outreach draft behind each lead card on a run receipt.
//
// WHAT THIS IS: a pure function that turns the facts we actually hold about a
// lead into a subject and a body a rep can paste straight into HubSpot's email
// composer. It is text, nothing else — there is no transport in this file, no
// fetch, no mailto, no send. The app never sends an email, and the way to keep
// that true is for the "email" to be a string a human carries somewhere else.
//
// TWO RULES GOVERN EVERY LINE BELOW.
//
// 1. NOTHING IS INVENTED. Every clause is assembled from a fact that was
//    present on the lead. An unknown city, trade or contact name does not
//    become a placeholder, a guess, or a vaguer version of itself — the clause
//    that would have carried it is simply not written. That is why the
//    sentences are built from parts rather than templated with slots: a
//    template needs something to put in the hole, and we would rather have no
//    hole. A first name is NEVER derived from an email local-part ("j.kelly@"
//    is not evidence that anyone is called J), so the greeting falls back to a
//    plain "Hi there".
//
// 2. ONE NUMBER, ATTRIBUTED. Revailo is per-seat AI masonry estimation
//    software that displaces manual takeoff or an incumbent tool, and the only
//    figure any of these emails may state is takeoff time cut by UP TO 70% vs
//    manual — written as one token below so no edit can quietly turn a ceiling
//    into a promise, and always framed as "Revailo says", because the source
//    material is explicit that this is a vendor claim and not an audited fact.
//
//    THE PER-MINUTE FIGURES ARE GONE. This file used to state "about 3.5
//    minutes for a global blueprint analysis" and "about 2 minutes per
//    floorplan" as permitted claims. They appear NOWHERE in the sales
//    material this app is written against — not in the playbook, not in the
//    sample emails, not in anything Bo sent — so they were two numbers the app
//    invented and put in a rep's mouth. Any figure added here later needs a
//    source first, the way the upstream draft server treats its release notes
//    as the only release claims allowed.
//
//    No case studies, no invented ROI, no deadline, no "last chance" — a rep
//    who pastes this is putting their own name on it.
//
// 3. NO STATED DURATIONS, EVER. Not "fifteen minutes", not "a 30-minute
//    walkthrough", not "30 minutes next week". The ask is always for their
//    availability, because none of the real processes — demo, onboarding —
//    take the round number an email would promise. Same reason there is no
//    "Hope all is well" and no "following up": the openers are substance, and
//    those two phrases are the first things a reader's eye skips.
//
// bodyHtml exists for one reason: HubSpot's composer keeps paragraphs when the
// clipboard carries text/html, and flattens them when it doesn't. So the HTML
// is deliberately the dullest markup possible — <p> with an inline
// Arial/Helvetica stack, no classes, no webfonts, no layout — because anything
// cleverer is stripped by the composer and lands as a mess in the sent mail.

import { isCallTouch, isEmailTouch } from './cadence.js'
import { taskMeaning } from './receiptSections.js'
import { greetingFor, packedPeople } from './greeting.js'

// The one hedged claim, written once. Every use reads this constant, so the
// ceiling can never drift into a flat "70%" — and every use frames it as the
// vendor claim it is (ATTRIBUTION below), never as something we measured.
const TIME_CLAIM = 'up to 70%'
const ATTRIBUTION = 'Revailo says'

// THE PRODUCT SENTENCE, verbatim from the rep's own rewrite of it. It names the
// materials (brick, block, veneer) instead of describing a category of
// software, and it ends on who stays in control — the pairing that must not be
// dropped, because a masonry estimator's first objection to AI is that it will
// take the quantities out of their hands.
const PRODUCT_SENTENCE =
  'Revailo uses AI to quantify brick, block, veneer, and other masonry materials directly from project plans.'
const CONTROL_SENTENCE =
  'This helps estimators complete takeoffs faster while maintaining full control of the quantities.'
// THE ASK, in the one form that names no duration — and whose WINDOW fits the
// day it is written. It used to be fixed at "this week or early next", and Bo
// raised it on a Friday (report 20260904-152356): a contractor reading "what
// does this week look like" on Friday morning concludes the writer does not
// know what day it is. The window is read off the America/Chicago weekday —
// Bo's calendar, the clock every other date in this app is read on — never the
// browser's zone, which for a rep drafting late on Friday from another zone
// could already be Saturday.
//
// The model-written drafts get the same rule from the enrich lambda
// (_availability_window in its handler.py), which computes the window in code
// and hands it to the model as an input field. Keep the two tables identical:
// a fallback template that disagrees with the model about what day it is would
// be a worse tell than either alone.
const CHI_WEEKDAY_FMT = new Intl.DateTimeFormat('en-US', { timeZone: 'America/Chicago', weekday: 'short' })

/**
 * The span the closing ask may name, for the Chicago weekday at `now`.
 * Monday to Wednesday still have a week to point at; Thursday keeps the week
 * but says "later", because "this week" on a Thursday is one working day;
 * Friday offers only next week (Bo's case); a weekend draft is read on Monday
 * at the earliest, and "next week" is the week the reader is standing in.
 * An unreadable `now` falls back to the wall clock rather than to a fixed
 * phrase — a wrong window is the bug this exists to prevent.
 */
export function availabilityWindow(now = Date.now()) {
  const d = now instanceof Date ? now : new Date(now)
  const day = CHI_WEEKDAY_FMT.format(Number.isNaN(d.getTime()) ? new Date() : d)
  if (day === 'Mon' || day === 'Tue' || day === 'Wed') return 'this week or early next'
  if (day === 'Thu') return 'later this week or early next'
  if (day === 'Fri') return 'early next week'
  return 'next week'
}

export function availabilityAsk(now = Date.now()) {
  return `What does your availability look like ${availabilityWindow(now)}?`
}

const clean = (value) => String(value ?? '').replace(/\s+/g, ' ').trim()

// A city_state value with any trailing ZIP (or ZIP+4) removed, for use in a
// SENTENCE. Only the trailing postal code goes: nothing else is reinterpreted,
// and a value that carries no ZIP comes back untouched.
const cityForProse = (location) => clean(String(location ?? '').replace(/\s+\d{5}(-\d{4})?\s*$/, ''))

// "Masonry; Concrete restoration" is one packed cell in the directory; the
// first entry is the trade we lead with. Everything after it is dropped rather
// than stuffed into a sentence that would read like a database row.
function firstOf(value) {
  return clean(String(value ?? '').split(';')[0])
}

// A Capitalized word mid-sentence reads like a proper noun it isn't
// ("about Masonry takeoffs"), but an acronym or a brand must survive. Only the
// plain Capitalized-then-lowercase shape is lowered.
function softLower(text) {
  return clean(text)
    .split(' ')
    .map((w) => (/^[A-Z][a-z]+$/.test(w) ? w.toLowerCase() : w))
    .join(' ')
}

// The packed `contacts` cell is a flat run of `name=…; role=…; phone=…` groups.
// We only ever want the first name in it, and only when it is really there.
function firstPackedContact(raw) {
  const m = String(raw ?? '').match(/(?:^|;)\s*name=([^;]+)/)
  return m ? clean(m[1]) : ''
}

// ── web research (the enrichment dossier), read at the boundary ─────────────
//
// A researched lead arrives with research_* convenience fields the engine
// merged off the enrichment dossier (OpenAI web search via /api/enrich), or a
// receipt stub carrying bounded copies of the same. Research text may carry
// inline "(per domain.com)" citations the model wrote; those are provenance,
// not prose — they are stripped HERE, at the one boundary where dossier text
// enters draft-land, so no parenthetical can ever surface in a subject or
// body. The domains themselves live in research_sources and are cited in
// sources[], where citations belong.

// Strip inline "(per domain.com)"-style citation parentheticals from researched
// text. Only parentheticals that READ as citations are touched — "(per …)",
// "(source: …)", "(via …)" — a parenthetical carrying a real fact survives.
export function stripInlineCitations(text) {
  return clean(
    String(text ?? '')
      .replace(/\s*\(\s*(?:per|source:?|via)\s+[^()]*\)/gi, '')
      .replace(/\s+([,.;!?])/g, '$1'),
  )
}

// A researched value longer than this cannot be one mechanical clause — it is
// a paragraph pretending to be a field, and the clause is simply not written.
// (The receipt stub caps these fields far lower; this bound only matters for a
// full dossier merged onto a row.)
const MAX_RESEARCH_CLAUSE = 240

// ── source LINKS: the pages the research actually opened ────────────────────
//
// The dossier's sources[] are whole URLs the model really read. They are the
// only thing in this app a reader can click through to and check for
// themselves, so they get one gatekeeper — and it is strict, because a link is
// the one value here that a browser will EXECUTE if we let it. Only http(s)
// survives: a `javascript:` (or `data:`, or `vbscript:`) "URL" is not a source
// and is never handed to a component as one. Anything unparseable is dropped
// whole; nothing is repaired, rewritten or guessed at.

const MAX_SOURCE_LINKS = 8

/**
 * One source link, or null. Returns the parsed href and the hostname a UI
 * labels it with (www. stripped) — never a partial or "fixed up" URL.
 * @returns {{href: string, host: string}|null}
 */
export function readSourceLink(value) {
  const raw = clean(value)
  if (!raw) return null
  let u
  try {
    u = new URL(raw)
  } catch {
    return null
  }
  // http/https ONLY. This is the line that keeps a javascript: payload out of
  // an href, and it is a whitelist rather than a blacklist on purpose.
  if (u.protocol !== 'http:' && u.protocol !== 'https:') return null
  const host = u.hostname.replace(/^www\./i, '')
  if (!host) return null
  return { href: u.href, host }
}

/**
 * Every source link in whichever shape the field arrives in: an array of URLs
 * (the engine's merged row) or one joined string (the receipt stub, which
 * packs whole URLs only). Splitting is on whitespace and on a comma that is
 * immediately followed by another scheme, so a URL carrying a comma is never
 * cut in half — and anything that does not survive readSourceLink is dropped
 * rather than shown as a broken link. Deduped by href, bounded.
 * @returns {{href: string, host: string}[]}
 */
export function readSourceLinks(value) {
  const parts = Array.isArray(value)
    ? value
    : String(value ?? '')
        .split(/\s+/)
        .flatMap((token) => token.split(/,(?=https?:\/\/)/))
  const out = []
  for (const part of parts) {
    const link = readSourceLink(String(part ?? '').replace(/[,;]+$/, ''))
    if (link && !out.some((x) => x.href === link.href)) out.push(link)
    if (out.length >= MAX_SOURCE_LINKS) break
  }
  return out
}

/**
 * The research signal on one lead, normalized from either shape it arrives in:
 * the engine's merged row (researched: true, researchMode: 'fresh'|'cached',
 * research_sources: array) or the receipt stub's bounded copies (researched:
 * 'fresh'|'cached', research_sources: a joined string). Unresearched leads —
 * every old receipt included — come back { researched: false } with every
 * field ''/[] so callers can treat absence uniformly.
 */
export function readResearch(lead = {}) {
  const mode =
    clean(lead.researchMode) ||
    (typeof lead.researched === 'string' ? clean(lead.researched) : '')
  const researched = lead.researched === true || mode === 'fresh' || mode === 'cached'
  const sources = (
    Array.isArray(lead.research_sources)
      ? lead.research_sources
      : String(lead.research_sources ?? '').split(',')
  )
    .map((d) => clean(d))
    .filter(Boolean)
  return {
    researched,
    // Only the two modes the contract defines are ever claimed; anything else
    // is researched-with-unknown-mode, and the mode clause is not written.
    mode: mode === 'fresh' || mode === 'cached' ? mode : '',
    at: clean(lead.researchedAt),
    sources,
    // The REAL pages the research opened, parsed and protocol-checked. [] when
    // the dossier carried none — and [] is then said out loud by the UI, never
    // papered over with an internal citation.
    sourceUrls: readSourceLinks(lead.research_source_urls),
    opener: stripInlineCitations(lead.research_opener),
    recentActivity: stripInlineCitations(lead.research_recent_activity),
    buyingSignal: stripInlineCitations(lead.research_buying_signal),
    currentSolution: stripInlineCitations(lead.research_current_solution),
    painPoints: stripInlineCitations(lead.research_pain_points),
    serviceArea: stripInlineCitations(lead.research_service_area),
  }
}

// ── what the thread already shows ───────────────────────────────────────────
//
// A follow-up template writes sentences that only hold up in silence: "wanted
// to make sure you saw…", "no reply", "a quick reply either way is
// appreciated". Nothing in this file could ever check that silence. The
// builders were handed a touch number and a last-touch date and nothing else,
// so they said "no reply" because the CADENCE implied it, not because anybody
// had read the mail — and the cadence is arithmetic over outbound sends, which
// is exactly the thing an inbound reply does not appear in. A lead who had
// written back could still be handed a touch-6 draft asking for "a quick reply
// either way", and a call sheet whose first line read "no reply".
//
// The per-lead message list now travels with the lead (mailHistory.js →
// agentRun → the receipt stub → here). Entries look like
//   { dir: 'out'|'in', at: ISO, subject, snippet, from, bounced, auto }
// newest first, capped at six. This reads ONE thing off it: did a person on
// their side actually write back? A bounce is our own mail server talking and
// an auto-reply is theirs — neither is a person, so neither counts as a reply.
//
// AN ABSENT LIST IS NOT EVIDENCE OF SILENCE. `null` means nobody handed the
// thread over (an old receipt, a lead with no mirrored record, a run from
// before this existed), and every clause below is then left exactly as it was
// — the same words, in the same order, as the day before this function
// existed. Only a list that is really there can change what gets written.
export function inboundReplyOf(lead = {}, opts = {}) {
  const rows = Array.isArray(opts?.history)
    ? opts.history
    : Array.isArray(lead?.history)
      ? lead.history
      : null
  if (!rows) return null
  for (const m of rows) {
    if (!m || typeof m !== 'object') continue
    if (m.dir !== 'in') continue
    // A bounce and an auto-responder are machines. Treating either as "they
    // replied" would trade one false claim for another.
    if (m.bounced || m.auto) continue
    return { at: clean(m.at), subject: clean(m.subject), from: clean(m.from) }
  }
  return null
}

// A greeting name has to look like a name. This rejects "Sales Dept",
// "info", "Estimating Team" style values by requiring a leading letter and
// nothing but letters, hyphens and apostrophes in the token we would use.
function greetingName(fullName) {
  const first = clean(fullName).split(' ')[0] || ''
  if (first.length < 2 || first.length > 24) return ''
  if (!/^[A-Za-zÀ-ɏ][A-Za-zÀ-ɏ'’-]*$/.test(first)) return ''
  const lower = first.toLowerCase()
  if (lower === 'the' || lower === 'team' || lower === 'sales' || lower === 'info') return ''
  return first
}

/**
 * WHO THE TEMPLATE SAYS HELLO TO — the same person the model is told to greet
 * (lib/greeting.js). The name the caller already decided wins: `greeting_name`
 * (the resolver's, handed to the fallback) or a receipt stub's `greeting`
 * (worked out from the full row when the receipt was packed). Otherwise the
 * rule runs over this lead's own fields, so a directory row greets the person
 * behind its address exactly as the model path would.
 */
function readGreetingName(lead, contact, company) {
  if (typeof lead.greeting_name === 'string') return clean(lead.greeting_name)
  const stored = lead.greeting
  if (stored && typeof stored === 'object' && typeof stored.name === 'string') return clean(stored.name)
  return greetingFor({
    email: clean(lead.email),
    contactName: contact,
    people: packedPeople(lead.contacts),
    company,
    website: clean(lead.website || lead.domain),
  }).name
}

/**
 * Everything the draft is allowed to know, read off one lead in whatever shape
 * it arrives (a receipt stub, a directory row, an enrichment result). Unknown
 * fields come back as empty strings — the callers below treat "" as "do not
 * write that clause", never as "write something vaguer".
 */
export function readFacts(lead = {}) {
  const company = clean(lead.company || lead.name || lead.companyName)
  const location = clean(
    lead.city_state ||
      lead.cityState ||
      lead.location ||
      [clean(lead.city), clean(lead.state)].filter(Boolean).join(', '),
  )
  const trade = softLower(firstOf(lead.trade || lead.trades))
  const contact = clean(
    lead.contactName || lead.contact_name || lead.estimator || firstPackedContact(lead.contacts),
  )
  return {
    company,
    location,
    trade,
    contact,
    first: readGreetingName(lead, contact, company),
    email: clean(lead.email),
    // The personalization fields, straight off the masonry directory row when
    // the xlsx research held them. '' when absent — and '' means the sentence
    // that would have carried the field is simply not written.
    serviceArea: clean(lead.service_area || lead.serviceArea),
    commercialResidential: clean(lead.commercial_residential || lead.commercialResidential),
    annualVolume: clean(lead.annual_volume || lead.annualVolume),
    enrichedSource: clean(lead.enriched_source || lead.enrichedSource),
    // The web-research signal, when the engine merged a dossier onto this lead
    // (or the stub carried its bounded copy). { researched: false } and all-''
    // otherwise — absence reads exactly like every other unknown fact.
    research: readResearch(lead),
  }
}

// ── personalization + sources ───────────────────────────────────────────────
//
// The "fun fact" sentence: AT MOST one extra sentence in the opener, assembled
// exactly the way the opener is — from a field actually held, verbatim, or not
// at all. Priority order, first present wins, because one specific sentence
// lands and three read like a database row.
//
// RESEARCHED FACTS OUTRANK ROW FACTS: when the enrichment dossier ran, what it
// found is fresher and more specific than the imported row, so it takes the
// one slot. A researched fact is usable ONLY when the research really
// happened (researched), really cited something (sources non-empty), and the
// value survives the citation strip inside one clause's length — anything
// less degrades to the row facts below, never to a vaguer sentence.
//
//   1. research_recent_activity   the researched value, verbatim, unlabelled
//   2. research_buying_signal     "
//   3. research_current_solution  "
//   4. research_opener            "
//   5. service_area               "You cover <area>."
//   6. commercial_residential     "You work on the commercial side." — ONLY
//      when the value clearly reads commercial; a value that also says
//      residential is not clearly commercial, so no sentence.
//   7. annual_volume              "You do <volume> in annual volume."
//
// A RESEARCHED FACT IS NOT LABELLED IN THE PROSE. It used to ship prefixed with
// "From our web research:", which advertised the machine inside the sentence
// the prospect reads. The provenance is unchanged and undiminished — the
// sources[] entry names the dossier and carries the pages it read, and the flow
// chart and the story both show them — it simply lives in the audit trail
// rather than in the paragraph.
//
// Never "on your website", never "as we discussed" (that one claims a
// conversation that may not have happened). "recently" appears ONLY when it is
// part of a verbatim researched value — the templates never add it, because the
// app's own voice must not imply a browse that, for a row fact, never happened.

// The labels a personalization source entry may carry — exported so the
// narrative (and any UI) can recognize the personalization fact among the
// other sources without re-deriving it. The researched labels come first;
// their entries carry the 'OpenAI web research · …' origin AND the dossier's
// real source URLs, which is what lets the UI link them.
//
// 'Pain points' stays in this list even though nothing picks it any more: an
// older receipt may still carry an entry labelled that way, and a researched
// entry must never be mistaken for a row fact.
export const RESEARCH_LABELS = [
  'Recent activity',
  'Buying signal',
  'Current solution',
  'Research opener',
  'Pain points',
  // The rest of the dossier's fields, added when the draft lambda stopped
  // relabelling every unrecognised research fact as "Research opener" — a
  // company-size fact was appearing in the audit trail under the name of the
  // opener sentence. Keep in sync with _DOSSIER_LABEL in
  // ../../../revailo-sales-platform-terraform/modules/lambda/python3.12/api-gateway/enrich/handler.py:
  // a label the lambda emits but this list omits reads as a ROW fact and
  // silently loses its source links.
  'Market footprint',
  'Offering mix',
  'Company size',
  'Years in business',
]
export const PERSONALIZATION_LABELS = [
  ...RESEARCH_LABELS,
  'Service area',
  'Commercial focus',
  'Annual volume',
]

// The origin string a researched-fact source entry carries: what ran, then the
// dossier's own source domains — the places the research actually read.
export function researchOrigin(domains = []) {
  const list = domains.filter(Boolean).join(', ')
  return list ? `OpenAI web research · ${list}` : 'OpenAI web research'
}

/**
 * The sources[] entry for the one personalization fact a draft used. A
 * RESEARCHED fact cites the web research, its display domains AND the real
 * URLs the research read — `urls` is what the UI turns into clickable links,
 * so it exists on researched entries ONLY. A row fact cites the import and
 * never carries a url: there is no page to open, and inventing one would be
 * the exact lie this file exists to prevent.
 */
function personalizationSource(personalization, origin) {
  const entry = {
    label: personalization.label,
    value: personalization.value,
    origin: personalization.researched ? researchOrigin(personalization.domains) : origin,
  }
  if (personalization.researched) {
    entry.urls = (personalization.links || []).map((l) => l.href)
  }
  return entry
}

/**
 * Does a FOLLOW-UP touch carry a personalization sentence at all? Only touch 2
 * does: on touch 3 (the trial nudge), 5 (the tools-objection preempt) and 6
 * (the concrete ask) a bolted-on company fact fights the hook's one thought.
 *
 * Exported because the provenance story needs it to stay honest. Without it, a
 * researched lead on touch 3 has a real dossier and no researched clause in the
 * email, and the only reading left is "the search found nothing" — which is a
 * false statement about the research. The truthful sentence is "this touch
 * carries no personal line", and that is what this function lets both story
 * builders say.
 */
export const followupPersonalizes = (touch) => touch === 2

/**
 * The one personalization sentence a lead's facts earn, or null when none of
 * the fields is held. Takes the OUTPUT of readFacts(). A researched pick
 * carries `researched: true`, the dossier's source `domains` (display text)
 * and its real source `links` so the caller can cite — and LINK — the actual
 * origin.
 * @returns {{text: string, label: string, value: string, researched?: boolean,
 *            domains?: string[], links?: {href: string, host: string}[]}|null}
 */
export function readPersonalization(facts = {}) {
  const r = facts.research
  // A researched pick needs the research to have really run and the value to
  // fit one clause — nothing more. It deliberately does NOT require citations:
  // a dossier that reported no source links still ran, and the honest move is
  // to use what it found and say plainly that it linked nothing (the UI does),
  // not to silently swap in a row fact under a researched label.
  if (r && r.researched) {
    const pick = [
      { value: r.recentActivity, label: 'Recent activity' },
      { value: r.buyingSignal, label: 'Buying signal' },
      { value: r.currentSolution, label: 'Current solution' },
      { value: r.opener, label: 'Research opener' },
    ].find((c) => c.value && c.value.length <= MAX_RESEARCH_CLAUSE)
    if (pick) {
      // THE FACT, UNLABELLED. This used to ship as "From our web research:
      // <fact>", which was honest and was also the single most machine-sounding
      // string in the product — a sentence no salesperson has ever written,
      // announcing its own provenance inside the prose. The provenance did not
      // go anywhere: it is on the sources[] entry below, in the flow chart, and
      // in the story, all of which name the dossier and link the pages it read.
      // A citation belongs in the audit trail, not in the paragraph a
      // contractor reads.
      //
      // The value is verbatim (already citation-stripped at the readResearch
      // boundary). No terminal period is added when the value brought its own.
      const text = /[.!?]$/.test(pick.value) ? pick.value : `${pick.value}.`
      return {
        text,
        label: pick.label,
        value: pick.value,
        researched: true,
        domains: r.sources,
        links: r.sourceUrls,
      }
    }
    // Research ran but produced no usable clause (empty fields, an over-long
    // value): fall through to the row facts — a degraded research never
    // produces a researched claim.
  }
  if (facts.serviceArea) {
    return { text: `You cover ${facts.serviceArea}.`, label: 'Service area', value: facts.serviceArea }
  }
  const cr = clean(facts.commercialResidential)
  if (cr && /commercial/i.test(cr) && !/residential/i.test(cr)) {
    return { text: 'You work on the commercial side.', label: 'Commercial focus', value: cr }
  }
  // A zero volume ("0", "$0", "0.0") is a value the row may genuinely hold —
  // receipt stubs stringify numeric cells — but "You do 0 in annual volume."
  // is a nonsense sentence, so the clause is simply not written. Rule 2 allows
  // dropping a clause; it never allows writing a silly one.
  if (facts.annualVolume && !/^\$?0+(\.0+)?$/.test(facts.annualVolume)) {
    return {
      text: `You do ${facts.annualVolume} in annual volume.`,
      label: 'Annual volume',
      value: facts.annualVolume,
    }
  }
  return null
}

// Where a cited value REALLY came from. Every field on a masonry row arrived
// via the directory import from the masonry-leads-advanced xlsx research; when
// the row's enriched_source names the enrichment origin (e.g. Apollo), the
// citation carries that too. Never any other origin — citing a source the
// value did not come from is worse than citing none.
function sourceOrigin(facts) {
  const base = 'Masonry directory import'
  const src = clean(facts.enrichedSource)
  if (!src) return base
  // enriched_source values that just restate the import itself (the demo
  // manifest stamps every row 'import') do NOT name an enrichment origin —
  // appending '· Import enrichment' would cite an enrichment that never
  // happened, which is exactly the false citation this function exists to
  // prevent. Only a value naming a real origin (e.g. 'apollo') earns a suffix.
  if (['import', 'imported', 'directory', 'xlsx'].includes(src.toLowerCase())) return base
  return `${base} · ${src.charAt(0).toUpperCase()}${src.slice(1)} enrichment`
}

/**
 * Build the subject and body for one lead.
 *
 * @param {object} lead  {name|company, email?, city_state?|city+state?, trade?,
 *                        contact_name?|contactName?|estimator?, contacts?}
 * @param {object} [opts]
 * @param {string} [opts.senderName]  signed off with, when we know it. Omitted
 *                                    entirely otherwise — the app appends the
 *                                    rep's saved signature at copy time
 *                                    (lib/signature.js), so a blank close is
 *                                    correct and a placeholder never is.
 * @returns {{subject: string, bodyText: string, bodyHtml: string,
 *            sources: {label: string, value: string, origin: string, urls?: string[]}[]}}
 *          `sources` lists every row-derived fact the email actually used —
 *          one entry per fact, each naming where the value really came from.
 */
export function buildOutreachEmail(lead = {}, opts = {}) {
  const f = readFacts(lead)
  const senderName = clean(opts.senderName)
  const ask = availabilityAsk(opts.now)

  const subject = f.company
    ? `Masonry takeoffs at ${f.company}`
    : 'Masonry takeoffs — Revailo'

  // One sentence assembled from the facts that exist — "I came across <company>
  // in <city> and your <trade> work." With none of them it is still a true,
  // complete sentence; each known fact only makes it more specific.
  //
  // It used to read "I'm reaching out to X in Y about masonry takeoffs", which
  // announced the email instead of opening it, and then said "takeoffs" one
  // sentence before the product line says it again. "I came across…" is the
  // rep's own opening move, and it is the one that earns the sentence after it.
  let opener = ''
  if (f.company) {
    opener = `I came across ${f.company}`
    // The city WITHOUT its ZIP. The row stores "Chicago, IL 60632" and a
    // sentence carrying the ZIP reads like a pasted database cell, which is the
    // specific thing a hand-written email must not sound like. The ZIP is still
    // on the row, still in the provenance; it just has no business in prose.
    const city = cityForProse(f.location)
    if (city) opener += ` in ${city}`
    opener += f.trade ? ` and your ${f.trade} work.` : '.'
  }

  // The personalization sentence, when the row earned one — appended inside
  // the opener paragraph, never anywhere else, never more than one.
  const personalization = readPersonalization(f)
  if (personalization) opener += ` ${personalization.text}`

  // THREE SHORT PARAGRAPHS, in the rep's own order: what the product does in
  // masonry nouns, an offer to run it on one of THEIR projects, and an ask for
  // their availability. What used to sit here was five paragraphs, two invented
  // per-minute figures, an unattributed 70%, per-seat licensing volunteered
  // before anyone asked, and a "worth fifteen minutes" close — a duration the
  // real process does not take.
  const paragraphs = [
    f.first ? `Hi ${f.first},` : 'Hi there,',
    // `opener` is '' for a lead with no company name at all, and the paragraph
    // then starts on the product sentence — which is exactly the rep's own cold
    // intro when he has nothing specific to open on.
    `${opener ? `${opener} ` : ''}${PRODUCT_SENTENCE} ${CONTROL_SENTENCE}`,
    f.company
      ? `I can run one of ${f.company}’s projects through it and walk your estimating team through the workflow together. ${ask}`
      : `I can run one of your projects through it and walk your estimating team through the workflow together. ${ask}`,
    senderName ? `Best,\n${senderName}` : 'Best,',
  ]

  const bodyText = paragraphs.join('\n\n')
  const bodyHtml = htmlBody(paragraphs)

  // Every row-derived fact the email above actually used, each citing where
  // the value really came from. A fact not used (or not held) gets no entry —
  // the array is the email's evidence, not the row's inventory.
  const origin = sourceOrigin(f)
  const sources = []
  if (f.company) sources.push({ label: 'Company name', value: f.company, origin })
  // The city AS WRITTEN, not the raw cell. The email says "Chicago, IL" where
  // the row says "Chicago, IL 60632", and a citation is worth most when a
  // reader can match it against the sentence in front of them character for
  // character. The full cell is still on the provenance view's row node, which
  // is where the unedited field belongs.
  if (f.location) {
    sources.push({ label: 'Location', value: cityForProse(f.location) || f.location, origin })
  }
  if (f.trade) sources.push({ label: 'Trade', value: f.trade, origin })
  // A researched personalization fact cites what really produced it — the web
  // research, its source domains and the pages it opened — never the directory
  // import it didn't come from. Row facts keep the import origin.
  if (personalization) sources.push(personalizationSource(personalization, origin))
  // The greeting name, only when the greeting actually used one. The value
  // cited is the contact field the name was read from — that is the fact held.
  if (f.first) sources.push({ label: 'Contact name', value: f.contact, origin })

  return { subject, bodyText, bodyHtml, sources }
}

// ── follow-up drafts (Stage 2 — "Follow-up / Value Bump") ───────────────────
//
// The same two rules as buildOutreachEmail, plus the three Stage-2 rules from
// Bo's playbook that make a follow-up a follow-up and not a resend:
//
//   1. FRESH SUBJECT PER TOUCH. Each touch is a new thread — never the touch-1
//      subject, never "Re:". The subject is built from the company name and the
//      fixed positioning only, so it can't leak a fact we don't hold.
//   2. ONE ROTATING HOOK PER TOUCH. Touch 2 leads with the estimating-bandwidth
//      thesis, touch 3 with the free trial on their own projects, touch 5
//      preempts the "we already have tools" objection, touch 6 makes the
//      concrete 30-minute ask with a graceful easy-out. Never the same ask
//      twice.
//   3. THE OPENER IS TRUTHFUL BY CONSTRUCTION. "Following up on my previous
//      email regarding Revailo" is only writable because this function refuses
//      any touch that isn't a follow-up EMAIL touch (2, 3, 5, 6 on the ladder).
//      A "follow-up" to someone never emailed would be a lie, so it throws.
//
// The fixed product numbers appear AT MOST once per email, and only where the
// hook calls for them — on this ladder that is touch 2, where the bandwidth
// thesis is the numbers' natural home. Touches 3, 5 and 6 carry no numbers.

const FOLLOWUP_EMAIL_TOUCHES = [2, 3, 5, 6]

// Chicago-calendar day label ("Aug 5") for a stored ISO timestamp — Bo works
// Central Time, so dates are read on his calendar. '' for an unreadable input,
// and '' means the clause that would have carried it is not written.
const CHI_DAY_FMT = new Intl.DateTimeFormat('en-US', {
  timeZone: 'America/Chicago',
  month: 'short',
  day: 'numeric',
})

export function formatChicagoDay(value) {
  if (!value) return ''
  const d = value instanceof Date ? value : new Date(value)
  return Number.isNaN(d.getTime()) ? '' : CHI_DAY_FMT.format(d)
}

// Fresh subject per touch, from the company + fixed positioning only. None of
// these is the touch-1 subject ("Masonry takeoffs at …"), and none can collide
// with another touch's.
function followupSubject(touch, company) {
  switch (touch) {
    case 2:
      return company ? `Estimating bandwidth at ${company}` : 'Estimating bandwidth — Revailo'
    case 3:
      return company
        ? `A free trial on ${company}’s own projects`
        : 'A free trial on your own projects — Revailo'
    case 5:
      return company
        ? `Fitting Revailo into the workflow at ${company}`
        : 'Fitting Revailo into your workflow'
    case 6:
      // No duration in the subject either — the linter on the source system
      // greps subjects and bodies alike for "<n> minutes".
      return company ? `Next step for ${company}` : 'Next step on Revailo'
    default:
      return ''
  }
}

/**
 * Build the follow-up email for one due lead — Stage 2 of Bo's playbook.
 *
 * @param {object} lead  same shapes buildOutreachEmail accepts
 * @param {object} opts
 * @param {number} opts.touch        REQUIRED: 2, 3, 5 or 6 — the follow-up
 *                                   email touches on the 7-touch ladder.
 *                                   Anything else throws: touch 1 is the cold
 *                                   intro (buildOutreachEmail), 4 and 7 are
 *                                   phone calls (buildCallPoints).
 * @param {number} [opts.daysSince]  business days since the last touch — not
 *                                   written into the email (a prospect is never
 *                                   told they're on a timer); provenance shows it.
 * @param {string} [opts.lastTouchAt] ISO of the last outbound — same: for
 *                                   provenance, not for the prospect.
 * @param {string} [opts.senderName] signed off with, when known.
 * @returns {{subject: string, bodyText: string, bodyHtml: string,
 *            sources: {label: string, value: string, origin: string, urls?: string[]}[]}}
 */
export function buildFollowupEmail(lead = {}, opts = {}) {
  const touch = opts.touch
  if (!FOLLOWUP_EMAIL_TOUCHES.includes(touch)) {
    throw new Error(
      `buildFollowupEmail: touch ${JSON.stringify(touch)} is not a follow-up email touch. ` +
        'Only touches 2, 3, 5 and 6 are follow-up emails — touch 1 is the cold intro ' +
        '(buildOutreachEmail) and touches 4 and 7 are phone calls (buildCallPoints). ' +
        'A "follow-up" to someone never emailed would be a lie, so this refuses to write one.',
    )
  }

  const f = readFacts(lead)
  const senderName = clean(opts.senderName)
  const ask = availabilityAsk(opts.now)
  const subject = followupSubject(touch, f.company)

  // NO SHARED OPENER. Every follow-up used to start with the same eleven words:
  // "Hope all is well. Following up on my previous email regarding Revailo."
  // It was truthful — the throw above guarantees a prior touch — and it was
  // still wrong three times over. The pleasantry is a warm-up line in front of
  // the substance; "following up" is a phrase the rep asked never to be used
  // ("use action phrases"); and the identical sentence on touches 2, 3, 5 and 6
  // is exactly the sameness he rejects in a batch. Each touch now opens on its
  // own hook, which is the only thing in the email that changed anyway.

  // Where a company-specific clause is called for but the company is unknown,
  // the sentence falls back to "your team" — a form of address, not a fact —
  // rather than inventing a name or leaving a hole.
  const forWhom = f.company ? `for ${f.company}` : 'for your team'

  // The personalization sentence rides ONLY on touch 2 (see
  // followupPersonalizes) — a held fact about their operation is what the
  // bandwidth thesis speaks to, so it leads into that hook naturally.
  const personalization = followupPersonalizes(touch) ? readPersonalization(f) : null

  // DID THEY ALREADY WRITE BACK? Read off the thread that now travels with the
  // lead (inboundReplyOf) — `null` whenever no thread was handed over, which is
  // the case for every receipt written before the list existed, and which
  // leaves every sentence below byte-for-byte as it was.
  //
  // Two clauses on this ladder presume silence, and both are re-written rather
  // than dressed up when the thread contradicts them. Neither replacement
  // claims to know what the reply SAID: the list carries a subject and a
  // ~200-character snippet, which is not enough to answer someone, and a
  // template that pretended otherwise would be inventing the one thing the rep
  // most needs to read for himself. So each says LESS — it drops the
  // presumption and keeps only what is true either way. The rep sees the reply
  // itself on the card, above this draft.
  const replied = inboundReplyOf(lead, opts)

  let hook = []
  switch (touch) {
    case 2:
      // The estimating-bandwidth thesis — the one touch that carries the
      // product numbers, because the thesis is what the numbers answer. The
      // personalization fact, when held, opens the paragraph: their operation,
      // then the thesis it makes concrete.
      hook = [
        `${personalization ? `${personalization.text} ` : ''}One theme we hear from estimating teams is that the challenge isn’t finding work, it’s having enough time to pursue all of it — takeoffs, revisions and quantity checks eat the day.`,
        `Revailo quantifies brick, block and veneer straight off the plans, so estimators spend less time generating quantities and more on pricing and scope. ${ATTRIBUTION} it cuts takeoff time ${TIME_CLAIM} against manual takeoff.`,
        f.company
          ? `Would it make sense to compare notes on how ${f.company} estimates today? ${ask}`
          : `Would it make sense to compare notes on how your team estimates today? ${ask}`,
      ]
      break
    case 3:
      // The free trial on THEIR OWN projects — Bo's central proof vehicle.
      //
      // "Wanted to make sure you saw the free trial" is an opener written for
      // an unanswered thread: it tells the reader we think our last email may
      // have gone unread. Say it to someone who has already written back and it
      // reads as though nobody here opened the reply. When the thread shows one,
      // the opener drops the presumption and simply names the subject.
      hook = [
        replied
          ? 'On the free trial: we set your team up to evaluate Revailo on your own projects — real plan sets, your estimators, running it next to however you estimate today.'
          : 'Wanted to make sure you saw the free trial: we set your team up to evaluate Revailo on your own projects — real plan sets, your estimators, running it next to however you estimate today.',
        'There’s no obligation on it. If you’d like trial access, a quick reply is all it takes and I’ll handle the setup.',
      ]
      break
    case 5:
      // The tools-objection preempt, then a walkthrough on a real project. The
      // ask names no duration — it asks what their week looks like.
      hook = [
        'You likely already have tools and processes that work today, so rather than pitch you over email, I’d rather show you how Revailo fits into the way your team already estimates — without changing how they work.',
        `The best way to see that is on a real project of yours, with your estimators. ${ask}`,
      ]
      break
    case 6:
      // The concrete ask, with the graceful easy-out — never a hard break-up.
      // "Let's schedule 30 minutes next week" became an availability question:
      // the round number was the one thing in this touch that promised
      // something none of the real processes deliver.
      //
      // The easy-out used to end "— a quick reply either way is appreciated",
      // which asks for the reply the thread may already contain. The clause
      // that asks for it is dropped; the easy-out itself is true whether or not
      // they have written, so it stays.
      hook = [
        `I'd like to walk through your current estimating process and see whether Revailo makes sense ${forWhom}. ${ask}`,
        replied
          ? 'And if now isn’t the right time, just let me know and I’d be happy to reconnect at a later date.'
          : 'And if now isn’t the right time, just let me know and I’d be happy to reconnect at a later date — a quick reply either way is appreciated.',
      ]
      break
  }

  const paragraphs = [
    f.first ? `Hi ${f.first},` : 'Hi there,',
    ...hook,
    senderName ? `Best,\n${senderName}` : 'Best,',
  ]

  const bodyText = paragraphs.join('\n\n')
  const bodyHtml = htmlBody(paragraphs)

  // The facts this follow-up actually used: the company (subject and, on some
  // touches, a body clause), the touch-2 personalization fact, the greeting
  // name. A follow-up never writes location or trade, so they are never cited.
  const origin = sourceOrigin(f)
  const sources = []
  if (f.company) sources.push({ label: 'Company name', value: f.company, origin })
  // Same rule as touch 1: a researched fact cites the web research, its
  // domains and its links; a row fact cites the import.
  if (personalization) sources.push(personalizationSource(personalization, origin))
  if (f.first) sources.push({ label: 'Contact name', value: f.contact, origin })

  return { subject, bodyText, bodyHtml, sources }
}

// ── call prep (touches 4 and 7 — the ladder's phone calls) ──────────────────
//
// The app NEVER dials. A call touch becomes 3–5 talking points and the best
// number we actually hold — Bo makes the call. Every point is built from a fact
// held (the cadence position, the real touch history, the directory row) or
// names itself as a step Bo takes; a fact not held is a sentence not written.

// The best number actually on the lead: the direct contact_phone column, or
// the first `phone=…` in the packed contacts blob. '' when genuinely unknown —
// never a placeholder, never a guess.
export function readPhone(lead = {}) {
  const direct = clean(lead.contact_phone || lead.contactPhone)
  if (direct) return direct
  const m = String(lead.contacts ?? '').match(/(?:^|;)\s*phone=([^;]+)/)
  return m ? clean(m[1]) : ''
}

/**
 * Talking points for one due CALL touch.
 *
 * @param {object} lead  same shapes buildOutreachEmail accepts
 * @param {object} opts
 * @param {number} opts.touch         REQUIRED: 4 or 7 — the ladder's phone
 *                                    calls. Anything else throws: call prep for
 *                                    an email touch would misstate the cadence.
 * @param {number} [opts.daysSince]   business days since the last touch.
 * @param {string} [opts.lastTouchAt] ISO of the last outbound touch.
 * @param {boolean} [opts.replyUnchecked] REQUIRED to be true whenever the run
 *                                    that admitted this lead could NOT read the
 *                                    inbox: the sheet then says "reply unknown"
 *                                    instead of "no reply". "No reply" is only
 *                                    true by construction when the reply freeze
 *                                    actually ran over a readable inbox.
 * @returns {{points: string[], phone: string}}  3–5 short strings + the number
 *                                    ('' when none is held).
 */
export function buildCallPoints(lead = {}, opts = {}) {
  const touch = opts.touch
  if (!isCallTouch(touch)) {
    throw new Error(
      `buildCallPoints: touch ${JSON.stringify(touch)} is not a call touch. ` +
        'Only touches 4 and 7 on the ladder are phone calls — email touches get a draft, not a dial sheet.',
    )
  }

  const f = readFacts(lead)
  const phone = readPhone(lead)

  // What actually preceded this call, read straight off the ladder: touch 4
  // follows 3 emails; touch 7 follows 5 emails and 1 call.
  let emails = 0
  let calls = 0
  for (let n = 1; n < touch; n++) {
    if (isEmailTouch(n)) emails += 1
    else if (isCallTouch(n)) calls += 1
  }

  // (a) The cadence context, from the real numbers we were handed. Each clause
  // only appears when its fact does. "No reply" used to be justified by
  // construction — the reply freeze reads the inbox, and a lead that replied is
  // frozen out before this is ever called — and when the caller said the inbox
  // could not be read (replyUnchecked) the sheet admitted "reply unknown"
  // instead. That reasoning is sound but it is an inference about a lane
  // somewhere else in the pipeline, and Bo dials off this first line.
  //
  // The thread itself is now in reach (inboundReplyOf), so the strongest
  // evidence goes first: a genuine inbound message in the handed-over history
  // OVERRIDES the freeze's silence, whichever way the freeze went. There is no
  // reading of the mail under which "no reply" and a reply in the thread are
  // both right, and putting a rep on the phone with the wrong one is how a
  // conversation gets restarted from the top. Absent history changes nothing:
  // the two clauses below are exactly what this sheet has always said.
  const day = formatChicagoDay(opts.lastTouchAt)
  const daysSince = Number.isFinite(opts.daysSince) ? opts.daysSince : null
  const sent = calls > 0 ? `${emails} emails and ${calls} call` : `${emails} emails`
  const replied = inboundReplyOf(lead, opts)
  const repliedDay = replied ? formatChicagoDay(replied.at) : ''
  const replyClause = replied
    ? `THEY REPLIED${repliedDay ? ` ${repliedDay}` : ''} — read the thread before dialing, this is not a cold follow-up`
    : opts.replyUnchecked
      ? 'reply unknown (the inbox could not be read, check the thread before dialing)'
      : 'no reply'
  let context = `Touch ${touch} of 7: ${sent} so far, ${replyClause}`
  if (day) context += `, last touch ${day}`
  if (daysSince !== null)
    context += ` (${daysSince} business day${daysSince === 1 ? '' : 's'} ago)`
  context += '. Keep it under two minutes.'

  const points = [context]

  // (b) Reference the emails openly — Bo's voicemail style. Both halves are
  // steps Bo takes, named as his.
  points.push(
    'Name the emails up front ("I sent you a couple of notes about Revailo"); if no answer, leave a voicemail and reference it in the next email.',
  )

  // (c) One company fact, only when the row actually carries one. No row fact,
  // no point — 4 points instead of 5 is the honest count.
  if (f.trade && f.location) {
    points.push(`On the row: ${f.trade} contractor in ${f.location}. Anchor the examples in their work.`)
  } else if (f.trade) {
    points.push(`On the row: ${f.trade} contractor. Anchor the examples in their work.`)
  } else if (f.location) {
    points.push(`On the row: based in ${f.location}. Anchor the examples in their work.`)
  }

  // (d) The one-sentence positioning, with at most ONE fixed number.
  points.push(
    `One-line positioning: ${PRODUCT_SENTENCE} ${CONTROL_SENTENCE} If asked for numbers: ${ATTRIBUTION} takeoff time drops ${TIME_CLAIM} against manual, and that is Revailo's own figure rather than one we measured.`,
  )

  // (e) The ask.
  points.push(
    'The ask: a walkthrough on a plan set of theirs, plus free trial access on their own projects. Ask what their week looks like; never name a number of minutes.',
    'Log the call in HubSpot afterwards: the cadence cannot advance until it is on the record.',
  )

  return { points, phone }
}

// ── customer check-in drafts (Bo's daily worklist — the customer lanes) ─────
//
// Two DETERMINISTIC template builders for the worklist's customer sections:
// Inactive Users (no software access in over seven days) and Expired
// Subscriptions. No model, no research, no network — the same rules as every
// builder above, applied to a different audience:
//
//   THESE ARE CUSTOMERS, NOT PROSPECTS. The recipient is a HubSpot CONTACT
//   record, so the name on the item is the contact's own name and the first
//   name provably belongs to the address — unlike a directory row, where
//   `contact_name` can be a colleague packed into the same cell. That is why
//   these greetings may use the first name where the prospect templates hold
//   back. The same greetingName() shape test still applies: "Sales Dept" is
//   not a first name whoever it belongs to, and falls back to "Hi there".
//
//   EVERY DATE COMES FROM THE ITEM. The one fact each email states — when
//   they last used the software, when the subscription ended — is read off
//   the item the lane judge built and formatted on Bo's calendar
//   (America/Chicago). An item with no readable date gets the dateless
//   sentence, which is still true, never an invented "since <guess>".
//
//   The sign-off is a bare "Best," on purpose: the app appends the rep's
//   saved signature at copy time (lib/signature.js), exactly as it does for
//   every other draft in this file. bodyHtml is deliberately NOT returned —
//   these drafts ride the run result, which is persisted with the chat
//   thread, and the card derives the HTML flavour at copy time through
//   htmlFromText(), the same way receipts do.

// The exec record a customer template draft carries — same shape as
// templateExec above (and the Lambda's enrich/handler.py#_exec), naming the
// one builder that wrote the text. No model, no prompt: fixed sentences plus
// the item's own facts, and the record says so.
function customerDraftExec(fn) {
  return {
    runtime: 'browser',
    language: 'javascript',
    file: TEMPLATE_FILE,
    calls: [fn, 'formatChicagoDay'],
    model: '',
    at: new Date().toISOString(),
  }
}

/**
 * The check-in draft for one INACTIVE USER — a customer whose
 * last_software_access is more than seven days old.
 *
 * @param {object} item  a customer lane item (lib/agentRun.js
 *                       judgeCustomerLanes): { email, name, company?,
 *                       lastSoftwareAccess? (ISO), … }
 * @returns {{subject: string, bodyText: string, exec: object}}
 */
export function inactiveUserDraft(item = {}) {
  const first = greetingName(clean(item.name))
  const day = formatChicagoDay(item.lastSoftwareAccess)
  const paragraphs = [
    first ? `Hi ${first},` : 'Hi there,',
    // The held fact, stated to the person it is about: last_software_access is
    // the CONTACT's own attribute, so "you haven't been in" is exactly what we
    // know — not "nobody at your company", which would claim more than the
    // field holds. Dateless variant when the item carries no readable date.
    day
      ? `Looks like you haven't been in Revailo since ${day}, so I wanted to check in. Is anything getting in the way?`
      : `Wanted to check in on how Revailo has been going for you. Is anything getting in the way?`,
    `If something isn't working the way you expect, or you're stuck anywhere, just reply here and I'll help you get it sorted.`,
    'Best,',
  ]
  return {
    subject: 'Everything OK with Revailo?',
    bodyText: paragraphs.join('\n\n'),
    exec: customerDraftExec('inactiveUserDraft'),
  }
}

/**
 * The reminder draft for one EXPIRED SUBSCRIPTION — a customer whose
 * subscription_end is already past.
 *
 * @param {object} item  a customer lane item: { email, name, company?,
 *                       subscriptionEnd? (ISO), … }
 * @returns {{subject: string, bodyText: string, exec: object}}
 */
/**
 * The fallback email for one of the rep's HubSpot TASKS (kind task_followup)
 * — written by the model on the receipt in the normal course; this stands in
 * only when POST /api/enrich/draft cannot answer, and says so in its exec.
 *
 * Deterministic and modest on purpose: it states the task in plain words
 * (receiptSections.js#taskMeaning), names the last real exchange when the
 * item holds one, and asks one easy question. It never claims anything the
 * item does not carry.
 *
 * @param {object} item  a task email item: { subject, name, recipient, dueAt,
 *                       lastOutboundAt?, lastInboundAt?, lastSubject?, notes? }
 * @returns {{subject: string, bodyText: string, exec: object}}
 */
export function taskFollowupDraft(item = {}) {
  const first = greetingName(clean(item.name))
  const task = clean(item.subject)
  const meaning = taskMeaning(task)
  const lastOut = formatChicagoDay(item.lastOutboundAt)
  const lastIn = formatChicagoDay(item.lastInboundAt)
  const lastSubject = clean(item.lastSubject)
  const about = meaning || (task ? `"${task.replace(/^fu\s+/i, '')}"` : 'where things stand')
  const opener = lastIn
    ? `Thanks again for getting back to me on ${lastIn}. I wanted to follow up on ${about}.`
    : lastOut
      ? `Following up on my note from ${lastOut}${lastSubject ? ` ("${lastSubject.slice(0, 60)}")` : ''} about ${about}.`
      : `I wanted to follow up on ${about}.`
  const ask = meaning.startsWith('getting a demo')
    ? 'Would a 20-minute walkthrough this week or next work for you? Name a time and I will send the invite.'
    : meaning.startsWith('moving the account')
      ? 'If Revailo is earning its keep, I can get the account moved over to a paid plan in a couple of minutes. Want me to set that up?'
      : meaning.startsWith('getting them set up')
        ? 'Is there anything in the way of getting started? Happy to jump on a quick call and walk through the first project together.'
        : 'Is there anything you need from me on this? A one-line reply is plenty.'
  const paragraphs = [first ? `Hi ${first},` : 'Hi there,', opener, ask, 'Best,']
  return {
    subject: meaning.startsWith('getting a demo')
      ? 'Revailo demo'
      : meaning.startsWith('moving the account')
        ? 'Next step with Revailo'
        : lastSubject
          ? `Re: ${lastSubject.slice(0, 80)}`
          : 'Following up',
    bodyText: paragraphs.join('\n\n'),
    exec: customerDraftExec('taskFollowupDraft'),
  }
}

export function expiredSubscriptionDraft(item = {}) {
  const first = greetingName(clean(item.name))
  const day = formatChicagoDay(item.subscriptionEnd)
  const paragraphs = [
    first ? `Hi ${first},` : 'Hi there,',
    // "Our records show" is the honest frame: the date comes from the mirror
    // of the portal, not from their billing system, and a records line a
    // customer can correct is better than an assertion they cannot.
    day
      ? `Our records show your Revailo subscription ended ${day}.`
      : `Our records show your Revailo subscription has ended.`,
    // No em dash: these drafts skip the humanize pass (they carry no dossier
    // text to clean), so they are written the way humanize would leave them.
    `If you'd like to get set back up, just reply here and I'll take care of it. And if something wasn't working for you, I'd like to hear about that too.`,
    'Best,',
  ]
  return {
    subject: 'Your Revailo subscription',
    bodyText: paragraphs.join('\n\n'),
    exec: customerDraftExec('expiredSubscriptionDraft'),
  }
}

// ── humanize ────────────────────────────────────────────────────────────────
//
// A JS port of the openclaw sales-tracker's humanize.py: the local "de-AI-tell"
// pass every outreach draft is run through before a human sees it. It does two
// things, in this order, and NOTHING that reaches the network — it is a string
// in, a cleaner string out.
//
//   1. Replace the typographic dead-giveaways of machine text with the
//      punctuation a person actually types: an em dash becomes a comma, an en
//      dash between two numbers becomes a hyphen and elsewhere a comma, an
//      ellipsis becomes three dots, curly quotes go straight, a non-breaking
//      space becomes a normal one. Then tidy the artefacts that leaves (a space
//      before a comma, a doubled comma).
//   2. FLAG — never silently rewrite — the wordy AI-tell vocabulary, so the
//      provenance view can say honestly how clean the draft already was. A
//      rewrite of a real word is a judgement call a human makes; the machine
//      only counts.
//
// buildOutreachEmail already writes in a plain voice, so on today's drafts this
// mostly converts one em dash ("Revailo — AI estimation") into a comma and
// straightens the apostrophes. That it does so little is the point: the report
// it returns is the evidence, not a decoration.
const AI_TELLS = [
  'leverage', 'utilize', 'seamless', 'robust', 'delve', 'elevate', 'streamline',
  "in today's", 'fast-paced', 'look no further', 'rest assured', 'furthermore',
  'moreover', 'that said', 'tapestry', 'testament', 'underscore', 'pivotal',
  'realm', 'spearhead', 'synergy', 'cutting-edge', 'game-chang', 'unlock',
  'empower', 'holistic', 'myriad', 'plethora', 'boast', 'nestled', 'navigate the',
  'at the end of the day', 'second to none', 'unparalleled', 'best-in-class',
  'revolutioniz', 'supercharge', 'turnkey', 'bespoke', 'dive into', 'ecosystem',
]

// Count the tells BEFORE any transform, on the raw text, lowercased — the
// transforms never touch these words, so the count is stable either way, but
// reading the raw keeps this honest about what the writer actually produced.
function scanTells(text) {
  const low = String(text ?? '').toLowerCase()
  return AI_TELLS.filter((w) => low.includes(w))
}

// One field's worth of cleaning, returning both the cleaned string and a tally
// of what had to be changed — the tally is what the provenance step reports.
function humanizeField(input) {
  const t0 = String(input ?? '')
  const tally = {
    emDashes: (t0.match(/—/g) || []).length,
    enDashes: (t0.match(/–/g) || []).length,
    curlyQuotes: (t0.match(/[‘’“”]/g) || []).length,
    ellipses: (t0.match(/…/g) || []).length,
  }
  let t = t0
    .replace(/—/g, ', ') // em dash -> comma
    .replace(/(\d)\s*–\s*(\d)/g, '$1-$2') // en dash between numbers -> hyphen
    .replace(/–/g, ', ') // any other en dash -> comma
    .replace(/…/g, '...') // ellipsis
    .replace(/[‘’]/g, "'") // curly single quotes -> straight
    .replace(/[“”]/g, '"') // curly double quotes -> straight
    .replace(/ /g, ' ') // non-breaking space -> normal
    // tidy the artefacts the dash->comma leaves behind
    .replace(/\s+,/g, ',')
    .replace(/,\s*,/g, ',')
    .replace(/,\s*\./g, '.')
    .replace(/[ \t]{2,}/g, ' ')
    .replace(/ +\n/g, '\n')
  return { text: t, tally }
}

/**
 * Run a built draft (from buildOutreachEmail) through the humanize pass.
 * Returns a NEW draft with the same shape — { subject, bodyText, bodyHtml } —
 * plus a `report` the provenance view reads:
 *   { emDashes, enDashes, curlyQuotes, ellipses, aiTells: string[] }
 * The bodyHtml is rebuilt from the cleaned paragraphs so the copy-pastable
 * HTML and the plain text can never drift from each other.
 *
 * A draft's `sources` array passes through UNTOUCHED — it is the record of
 * which row fields the draft used and where each value came from, and a
 * punctuation pass has no business rewriting evidence.
 */
export function humanizeDraft(draft = {}) {
  const subject = humanizeField(draft.subject)
  const body = humanizeField(draft.bodyText)
  const paragraphs = body.text.split(/\n{2,}/)
  const bodyHtml = htmlBody(paragraphs)
  const report = {
    emDashes: subject.tally.emDashes + body.tally.emDashes,
    enDashes: subject.tally.enDashes + body.tally.enDashes,
    curlyQuotes: subject.tally.curlyQuotes + body.tally.curlyQuotes,
    ellipses: subject.tally.ellipses + body.tally.ellipses,
    aiTells: scanTells(body.text),
  }
  const out = { subject: subject.text, bodyText: body.text, bodyHtml, report }
  if (Array.isArray(draft.sources)) out.sources = draft.sources
  return out
}

function escapeHtml(text) {
  return String(text)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

// Standard email markup on purpose: an inline Arial/Helvetica/sans-serif stack,
// a normal 14px size, ordinary paragraph spacing. No class attribute anywhere —
// a pasted class points at a stylesheet the recipient's mail client will never
// have, and HubSpot strips it on the way out regardless.
const P_STYLE =
  'margin:0;font-family:Arial,Helvetica,sans-serif;font-size:14px;line-height:1.5;color:#000000;'

/**
 * The whole body as ONE styled div, paragraphs separated by a literal blank
 * line (`<br><br>`).
 *
 * This used to emit a `<p style="margin:0 0 12px">` per paragraph and rely on
 * that bottom margin for the gap — which is exactly what Gmail and HubSpot's
 * composers strip on paste. The inline style went, the margin went with it, and
 * every paragraph ran together into a wall of text. `<br>` is the one thing no
 * composer rewrites, so the spacing is now structural rather than cosmetic.
 */
function htmlBody(paragraphs) {
  const inner = paragraphs
    // A hard newline inside a paragraph (the sign-off) is a single break.
    .map((text) => escapeHtml(text).replace(/\n/g, '<br>'))
    .join('<br><br>')
  return `<div style="${P_STYLE}">${inner}</div>`
}

// ── the exec record for a TEMPLATE-built draft ──────────────────────────────
//
// The same shape the Lambda routes return (enrich/handler.py#_exec), so a
// receipt's Developer view renders every draft the same way, whichever code
// wrote it. This one says, plainly: this email was assembled IN THE BROWSER by
// the fixed sentences in this file — no model, no prompt — and, when it was a
// fallback, why the model did not write it. Kept next to the builders so it
// cannot describe code that is not here.
export const TEMPLATE_FILE = 'src/lib/outreachDraft.js'
export function templateExec(kind = 'initial', reason = '') {
  const out = {
    runtime: 'browser',
    language: 'javascript',
    file: TEMPLATE_FILE,
    calls: [
      'readFacts',
      'readPersonalization',
      kind === 'followup' ? 'buildFollowupEmail' : 'buildOutreachEmail',
      'humanizeDraft',
    ],
    model: '',
    at: new Date().toISOString(),
  }
  if (reason) out.fallback = String(reason).slice(0, 200)
  return out
}

/**
 * The clipboard HTML for a bodyText — the same markup buildOutreachEmail emits,
 * derived rather than stored. A saved receipt keeps only bodyText (the field
 * that dominates its size); the card rebuilds the HTML flavour at copy time
 * through THIS function, so a pasted draft is byte-identical whether it came
 * from a live run or a receipt reloaded a week later.
 */
export function htmlFromText(bodyText) {
  return htmlBody(String(bodyText ?? '').split(/\n{2,}/).filter(Boolean))
}

export default buildOutreachEmail
